import { create } from "zustand";

import type { LicenceStatus } from "../api/types";
import type { Notice } from "../state/ui";

/** Lecture seule d'une licence expirée (décision d'Alexis du 03/10/2026) : pendant les sept jours qui suivent
 *  l'échéance, Generex reste consultable et ses téléchargements restent permis ; toute écriture est refusée par le
 *  serveur (403 `LICENCE_READ_ONLY`). Ensuite, l'écran d'activation, comme avant. Un renouvellement garde le même
 *  code : dès que le serveur rend la licence active, tout redevient modifiable, sans rien ressaisir.
 *
 *  - Bandeau calme et durable (`ReadOnlyCallout`) : « Licence expirée le … : lecture seule jusqu'au … », avec
 *    « Voir la licence ».
 *  - Une action bloquée n'empile pas les avis : le premier refus de la lecture seule l'explique une fois (avis), les
 *    suivants désignent le bandeau ; l'avis d'échec propre à l'action, émis juste après le refus, est écarté
 *    (`echoOfRefusal`, lu par `notify`). Les encadrés d'erreur des fiches gardent leur message, près de l'action.
 *  - La file d'enregistrement de l'OPO et le brouillon des Données initiales gardent la saisie sur ce poste, sans
 *    nouvel essai ; la file repart d'elle-même au retour de la licence (`LicenceGate`). */

/** Échéance passée et fin de la lecture seule (ISO 8601). */
export interface ReadOnlyInfo {
  expiredAt: string;
  until: string;
}

/** Lecture seule en cours d'après l'état de la licence ; null sinon (licence active, fermée, ou serveur plus ancien). */
export function readOnlyInfo(status: LicenceStatus | undefined | null): ReadOnlyInfo | null {
  if (!status?.enforced || !status.licensed) return null;
  if (!status.read_only && status.state !== "grace") return null;
  if (!status.expired_at || !status.read_only_until) return null;
  return { expiredAt: status.expired_at, until: status.read_only_until };
}

/** D'un état de la licence au suivant : entrée en lecture seule, ou retour au travail (« resume » : les files
 *  d'enregistrement repartent, l'avis le dit). La fermeture (délai passé) n'est pas un retour : l'écran d'activation
 *  prend le relais. */
export function readOnlyTransition(
  before: LicenceStatus | undefined | null,
  after: LicenceStatus | undefined | null,
): "enter" | "resume" | null {
  const was = readOnlyInfo(before) !== null;
  const is = readOnlyInfo(after) !== null;
  if (!was && is) return "enter";
  if (was && !is && Boolean(after?.licensed)) return "resume";
  return null;
}

/** Délai pendant lequel un avis d'échec qui suit un refus de lecture seule est tenu pour l'écho de ce refus (même
 *  action : la mutation signale son échec juste après la réponse du serveur). */
export const ECHO_MS = 1500;
/** Cible du bandeau, désignée à chaque action bloquée : barre latérale (ou son rail), ou accueil. */
export const BANNER_TARGET = "licence-read-only";
export const HOME_BANNER_TARGET = "licence-read-only-home";

interface ReadOnlyState {
  /** Fiche de la licence ouverte depuis le bandeau ou l'avis. */
  panelOpen: boolean;
  /** Lecture seule déjà expliquée par un avis depuis son début. */
  explained: boolean;
  /** Dernier refus `LICENCE_READ_ONLY` reçu (horodatage, ms). */
  refusedAt: number;
  openPanel: () => void;
  closePanel: () => void;
  /** Hors lecture seule (licence active ou fermée) : la prochaine lecture seule s'expliquera de nouveau. */
  reset: () => void;
}

export const useReadOnly = create<ReadOnlyState>((set) => ({
  panelOpen: false,
  explained: false,
  refusedAt: 0,
  openPanel: () => set({ panelOpen: true }),
  closePanel: () => set({ panelOpen: false }),
  reset: () => set({ explained: false, refusedAt: 0 }),
}));

/** Refus d'une écriture par la lecture seule : « notice » la première fois (avis qui l'explique), « designate »
 *  ensuite (le bandeau est désigné, sans nouvel avis). `visible` faux (avis cachés, sous l'accueil) : « designate »,
 *  et l'explication reste à donner au prochain refus. */
export function readOnlyRefusal(now = Date.now(), visible = true): "notice" | "designate" {
  const { explained } = useReadOnly.getState();
  if (!visible) {
    useReadOnly.setState({ refusedAt: now });
    return "designate";
  }
  useReadOnly.setState({ explained: true, refusedAt: now });
  return explained ? "designate" : "notice";
}

/** Nouvel état de la licence vu par le client : hors lecture seule (licence active ou fermée), la prochaine lecture
 *  seule s'expliquera de nouveau. Son début ne remet rien à zéro : le refus qui a fait relire l'état (licence encore
 *  « active » dans le navigateur) l'a peut-être déjà expliquée (revue du 03/10/2026). */
export function licenceStatusSeen(status: LicenceStatus | undefined | null): void {
  if (status && !readOnlyInfo(status)) useReadOnly.getState().reset();
}

/** Avis d'échec (`error`, `warn`) émis juste après un refus de lecture seule : l'écho de ce refus, écarté (le refus
 *  est déjà expliqué ou désigné). */
export function echoOfRefusal(tone: Notice["tone"], now = Date.now()): boolean {
  const { refusedAt } = useReadOnly.getState();
  return (tone === "error" || tone === "warn") && refusedAt > 0 && now - refusedAt < ECHO_MS;
}
